import ImportantContactForm from '../ImportantContactForm'
import { serviceUserProfileConstants as supConstants } from 'locale/serviceUserProfile'
import { useTranslation } from 'react-i18next'
import { NAMESPACE } from 'locale'
import { LPATypes, ResidentBody, ResidentItem } from '@lmc/ts-common'
import { Spinner } from 'components/generic'
import { UpdateResidentReturnType } from 'hooks/useResident'
import { Navigate, useNavigate } from 'react-router-dom'
import {
  ImportantContactBodyDtos,
  ImportantContactDtos
} from '@lmc/ts-common/lib/dtos/Resident'
import FamilyPortalFormCard, {
  FamilyPortalFormType
} from './components/FamilyPortalFormCard'
import ContactDetailsFormCard, {
  ContactDetailsFormType
} from './components/ContactDetailsFormCard'
import { ReactNode } from 'react'
import useFeatures from 'hooks/useFeatures'
import {
  Checkbox,
  CheckboxGroup,
  DatePicker,
  FormCard,
  FormItem,
  Input,
  TextArea
} from '@lmc/ui-kit'
import useRequiredRule from './utils/useRequiredRule'
import dayjs from 'dayjs'

export type BaseFormType = {
  firstName: string
  lastName: string
} & FamilyPortalFormType &
  ContactDetailsFormType &
  Pick<
    ImportantContactBodyDtos,
    'lpaTypes' | 'lpaFormLocation' | 'lpaDateAppointed'
  >

type PersonalContactFormProps<T> = {
  onSubmit: (values: Partial<ResidentBody>) => Promise<UpdateResidentReturnType>
  resident: ResidentItem | undefined
  isLoading: boolean
  editedIndex: number | undefined
  children: ReactNode
  getNewContact: (values: T) => ImportantContactBodyDtos
  title: string
  testId?: string
}

/**
 * This form has all common elements between Personal and Professional form
 * it's used in both PersonalContactForm and ProfessionalContactForm
 */
const BaseContactForm = <T extends BaseFormType>({
  onSubmit,
  resident,
  isLoading,
  editedIndex,
  children,
  getNewContact,
  title,
  testId
}: PersonalContactFormProps<T>) => {
  const { t } = useTranslation(NAMESPACE.SERVICE_USER_PROFILE)

  const features = useFeatures()
  const requiredRule = useRequiredRule()

  const initialValues = getInitialValues<T>(
    resident?.importantContacts,
    editedIndex
  )
  const onUpdate = (values: T) => {
    if (!resident) {
      throw new Error('Resident is undefined')
    }

    const newContact = getNewContact(values)

    const updatedContact: ImportantContactBodyDtos = {
      ...newContact,
      lpa: Boolean(newContact.lpaTypes && newContact.lpaTypes?.length > 0)
    }

    const residentBody = convertFormTypeToPartialResidentBody(
      updatedContact,
      editedIndex,
      resident.importantContacts
    )

    return onSubmit(residentBody)
  }

  const navigate = useNavigate()
  const residentID = resident?.id

  const onDelete = async () => {
    if (resident === undefined) {
      throw new Error('Resident is undefiend')
    }
    if (editedIndex === undefined) {
      throw new Error('Resident is undefiend')
    }
    const importantContacts =
      resident?.importantContacts as ImportantContactBodyDtos[]

    importantContacts.splice(editedIndex, 1)

    const residentBody = {
      importantContacts
    }

    await onSubmit(residentBody)

    navigate(`/residents/${residentID}/important-people`)
  }

  if (isLoading) {
    return <Spinner />
  }

  if (!resident) {
    return <Navigate to="/residents" />
  }

  const canDelete = editedIndex !== undefined

  return (
    <ImportantContactForm<T>
      title={title}
      initialValues={initialValues}
      updateResident={onUpdate}
      onDelete={canDelete ? onDelete : undefined}
    >
      <FormCard title={t(supConstants.GENERAL_INFORMATION)} testId={testId}>
        <FormItem
          required
          rules={[
            requiredRule(t(supConstants.FIRST_NAME)),
            {
              max: 35,
              message: t(supConstants.FIRST_NAME_MAX_LENGTH)
            }
          ]}
          label={t(supConstants.FIRST_NAME)}
          name="firstName"
        >
          <Input maxLength={35} />
        </FormItem>
        <FormItem
          required
          rules={[
            requiredRule(t(supConstants.LAST_NAME)),
            {
              max: 35,
              message: t(supConstants.LAST_NAME_MAX_LENGTH)
            }
          ]}
          label={t(supConstants.LAST_NAME)}
          name="lastName"
        >
          <Input maxLength={35} />
        </FormItem>
        {children}
        <FormItem
          label={t(supConstants.PROXY_FOR_SUBJECT_OF_CARE)}
          name="proxyForSubjectOfCare"
          valuePropName="checked"
        >
          <Checkbox />
        </FormItem>
        <FormItem
          noStyle
          shouldUpdate={(prevValues, currentValues) =>
            prevValues?.proxyForSubjectOfCare !==
            currentValues?.proxyForSubjectOfCare
          }
        >
          {({ getFieldValue }) =>
            getFieldValue('proxyForSubjectOfCare') ? (
              <FormItem
                label={t(supConstants.LOCATION_OF_DOCUMENT)}
                name="locationOfProxyDocument"
              >
                <Input />
              </FormItem>
            ) : null
          }
        </FormItem>
        <FormItem
          label={t(supConstants.LASTING_POWER_OF_ATTORNEY)}
          name="lpaTypes"
        >
          <CheckboxGroup>
            <Checkbox
              value={LPATypes.HEALTH_AND_WELLBEING}
              name={LPATypes.HEALTH_AND_WELLBEING}
            >
              {t(supConstants[LPATypes.HEALTH_AND_WELLBEING])}
            </Checkbox>
            <Checkbox value={LPATypes.FINANCIAL} name={LPATypes.FINANCIAL}>
              {t(supConstants[LPATypes.FINANCIAL])}
            </Checkbox>
          </CheckboxGroup>
        </FormItem>
        <FormItem
          label={t(supConstants.LPA_DATE_APPOINTED)}
          name="lpaDateAppointed"
          getValueProps={(value) => ({
            // convert string to dayjs object for DatePicker
            value: value ? dayjs(value, 'YYYY-MM-DD') : undefined
          })}
          normalize={(value) => {
            // Store as date-only string to avoid timezone issues
            return value ? dayjs(value).format('YYYY-MM-DD') : undefined
          }}
        >
          <DatePicker format="DD-MM-YYYY" />
        </FormItem>
        <FormItem
          label={t(supConstants.LPA_FORM_LOCATION)}
          name="lpaFormLocation"
        >
          <Input placeholder={t(supConstants.LPA_FORM_LOCATION_PLACEHOLDER)} />
        </FormItem>
        <FormItem
          label={t(supConstants.OBSERVATION_ABOUT_THIS_PERSON)}
          name="observations"
        >
          <TextArea
            autoSize={{
              minRows: 3
            }}
          />
        </FormItem>
      </FormCard>
      <ContactDetailsFormCard />
      {features?.family?.enabled && (
        <FamilyPortalFormCard resident={resident} editedIndex={editedIndex} />
      )}
    </ImportantContactForm>
  )
}

const getInitialValues = <T extends BaseFormType>(
  contacts: ImportantContactDtos[] | undefined,
  editedIndex: number | undefined
): Partial<T> | undefined => {
  if (editedIndex === undefined) {
    return undefined
  }
  if (contacts === undefined) {
    return undefined
  }

  const editedContact = contacts[editedIndex] as any

  const initialValues: Partial<T> = {
    ...editedContact,
    firstName: editedContact?.name?.first,
    lastName: editedContact?.name?.last
  }

  return initialValues
}

export const convertFormTypeToPartialResidentBody = (
  newContact: ImportantContactBodyDtos,
  editedIndex: number | undefined,
  importantContacts: ImportantContactDtos[] | undefined
): { importantContacts: ImportantContactBodyDtos[] } => {
  let updatedContactsArray: ImportantContactBodyDtos[] = []

  if (importantContacts?.length && importantContacts?.length > 0) {
    updatedContactsArray = [
      // we have a few legacy fields that were not required in ImportantContactDtos, but are required now in ImportantContactBodyDtos
      ...(importantContacts as unknown as ImportantContactBodyDtos[])
    ]
  }

  // is edit mode
  if (editedIndex !== undefined) {
    updatedContactsArray[editedIndex] = newContact
    // is add mode
  } else {
    updatedContactsArray.push(newContact)
  }

  return {
    importantContacts: updatedContactsArray
  }
}

export default BaseContactForm
